import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

interface EligibilityRecord {
  profile_id: number;
  full_name: string;
  member_number: string;
  dojo_name: string;
  current_rank_name: string;
  current_rank_index: number;
  instructor_approval: string;
  last_grading_date: string;
  last_grading_status: string;
  earliest_resit_date: string;
  is_financially_current: number;
  resit_delay_met: number;
  is_eligible: number;
}

type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 50;

const Eligibility: React.FC = () => {
  const [allData, setAllData] = useState<EligibilityRecord[]>([]);
  const [filtered, setFiltered] = useState<EligibilityRecord[]>([]);
  const [dojos, setDojos] = useState<string[]>([]);
  const [ranks, setRanks] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<keyof EligibilityRecord>('current_rank_index');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedDojos, setSelectedDojos] = useState<string[]>([]);
  const [selectedRanks, setSelectedRanks] = useState<string[]>([]);
  const [selectedEligibility, setSelectedEligibility] = useState<string[]>([]);
  const [selectedApproval, setSelectedApproval] = useState<string[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/reports/gradings/eligibility');
      const data = res.data.data as EligibilityRecord[];
      setAllData(data);
      setFiltered(data);
      setTotalCount(data.length);

      setDojos(Array.from(new Set(data.map((d) => d.dojo_name))).sort());

      const rankMap: { [key: string]: number } = {};
      data.forEach((d) => {
        if (!rankMap[d.current_rank_name]) {
          rankMap[d.current_rank_name] = d.current_rank_index;
        }
      });
      setRanks(Object.keys(rankMap).sort((a, b) => rankMap[a] - rankMap[b]));
    } catch (err) {
      setError('Failed to load eligibility data — please try again');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let result = allData;

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (d) =>
          (d.full_name || '').toLowerCase().includes(s) ||
          (d.member_number || '').toLowerCase().includes(s) ||
          (d.dojo_name || '').toLowerCase().includes(s) ||
          (d.current_rank_name || '').toLowerCase().includes(s)
      );
    }

    if (selectedDojos.length > 0) result = result.filter((d) => selectedDojos.includes(d.dojo_name));
    if (selectedRanks.length > 0) result = result.filter((d) => selectedRanks.includes(d.current_rank_name));
    if (selectedEligibility.length > 0) {
      result = result.filter((d) => {
        if (selectedEligibility.includes('Eligible') && d.is_eligible === 1) return true;
        if (selectedEligibility.includes('Not Eligible') && d.is_eligible === 0) return true;
        return false;
      });
    }
    if (selectedApproval.length > 0) {
      result = result.filter((d) => selectedApproval.includes(d.instructor_approval));
    }

    result = [...result].sort((a, b) => {
      let av: any;
      let bv: any;
      if (sortField === 'current_rank_name') {
        av = a.current_rank_index;
        bv = b.current_rank_index;
      } else {
        av = a[sortField] !== undefined ? a[sortField] : '';
        bv = b[sortField] !== undefined ? b[sortField] : '';
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    setFiltered(result);
    setTotalCount(result.length);
    setPage(1);
  }, [search, selectedDojos, selectedRanks, selectedEligibility, selectedApproval, sortField, sortDir, allData]);

  const toggleSort = (field: keyof EligibilityRecord) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field: keyof EligibilityRecord) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters = search || selectedDojos.length > 0 || selectedRanks.length > 0 ||
    selectedEligibility.length > 0 || selectedApproval.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedDojos([]);
    setSelectedRanks([]);
    setSelectedEligibility([]);
    setSelectedApproval([]);
    setPage(1);
  };

  const eligibleCount = allData.filter((d) => d.is_eligible === 1).length;
  const notEligibleCount = allData.filter((d) => d.is_eligible === 0).length;
  const approvedCount = allData.filter((d) => d.instructor_approval === 'Approved').length;
  const duesCurrentCount = allData.filter((d) => d.is_financially_current === 1).length;
  const resitDelayCount = allData.filter((d) => d.resit_delay_met === 0).length;

  const exportExcel = () => {
    setExporting(true);
    try {
      const headers = [
        'Member Number', 'Full Name', 'Dojo', 'Current Rank',
        'Instructor Approval', 'Dues Current', 'Resit Delay Met',
        'Last Grading Date', 'Last Result', 'Earliest Resit Date', 'Eligible',
      ];

      const rows = filtered.map((d) => [
        d.member_number || '',
        d.full_name,
        d.dojo_name,
        d.current_rank_name,
        d.instructor_approval,
        d.is_financially_current ? 'Yes' : 'No',
        d.resit_delay_met ? 'Yes' : 'No',
        d.last_grading_date ? new Date(d.last_grading_date).toLocaleDateString('en-GB') : '',
        d.last_grading_status || '',
        d.earliest_resit_date ? new Date(d.earliest_resit_date).toLocaleDateString('en-GB') : '',
        d.is_eligible ? 'Yes' : 'No',
      ]);

      const bom = '\uFEFF';
      const csv = bom + [headers, ...rows]
        .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NASK_Eligibility_' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const MultiSelect: React.FC<{
    label: string;
    id: string;
    options: string[];
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }> = ({ label, id, options, selected, setSelected }) => {
    const isOpen = openDropdown === id;
    return (
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>{label}</label>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
            style={{ ...styles.multiSelectBtn, borderColor: selected.length > 0 ? '#cc0000' : '#3a3a3a' }}
          >
            <span>{selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : selected.length + ' selected'}</span>
            <span style={{ fontSize: '10px' }}>▼</span>
          </button>
          {isOpen && (
            <div style={styles.multiSelectDropdown} onClick={(e) => e.stopPropagation()}>
              {options.map((opt) => (
                <label key={opt} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleMulti(opt, setSelected)}
                    style={{ marginRight: '8px', accentColor: '#cc0000' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} onClick={() => setOpenDropdown(null)}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Grading Eligibility</h1>
          <p style={styles.subtitle}>
            {loading ? 'Loading...' : totalCount.toLocaleString() + ' members'}
          </p>
        </div>
        <button
          onClick={exportExcel}
          style={exporting ? styles.exportBtnDisabled : styles.exportBtn}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : '⬇️ Export to Excel'}
        </button>
      </div>

      {/* Stats Cards */}
      {!loading && !error && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>Total Members</p>
            <p style={{ ...styles.statValue, color: '#3b82f6' }}>{allData.length.toLocaleString()}</p>
          </div>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>Fully Eligible</p>
            <p style={{ ...styles.statValue, color: '#22c55e' }}>{eligibleCount.toLocaleString()}</p>
            <p style={styles.statSub}>All 3 conditions met</p>
          </div>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>Not Eligible</p>
            <p style={{ ...styles.statValue, color: '#cc0000' }}>{notEligibleCount.toLocaleString()}</p>
          </div>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>Instructor Approved</p>
            <p style={{ ...styles.statValue, color: '#22c55e' }}>{approvedCount.toLocaleString()}</p>
            <p style={styles.statSub}>canGrade = 1</p>
          </div>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>Dues Current</p>
            <p style={{ ...styles.statValue, color: '#22c55e' }}>{duesCurrentCount.toLocaleString()}</p>
          </div>
          <div style={styles.statCard}>
            <p style={styles.statLabel}>In Resit Delay</p>
            <p style={{ ...styles.statValue, color: '#f59e0b' }}>{resitDelayCount.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          {error}
          <button onClick={fetchData} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Filters */}
      {!loading && !error && (
        <div style={styles.filtersCard} onClick={(e) => e.stopPropagation()}>
          <div style={styles.filtersRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Search</label>
              <input
                type="text"
                placeholder="Name, member no., dojo, rank..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <MultiSelect label="Dojo" id="dojo" options={dojos} selected={selectedDojos} setSelected={setSelectedDojos} />
            <MultiSelect label="Current Rank" id="rank" options={ranks} selected={selectedRanks} setSelected={setSelectedRanks} />
            <MultiSelect label="Eligibility" id="eligibility" options={['Eligible', 'Not Eligible']} selected={selectedEligibility} setSelected={setSelectedEligibility} />
            <MultiSelect label="Approval" id="approval" options={['Approved', 'Not Approved']} selected={selectedApproval} setSelected={setSelectedApproval} />
            {hasFilters && (
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>&nbsp;</label>
                <button onClick={clearFilters} style={styles.clearBtn}>Clear All</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={styles.loadingBox}>Loading eligibility data...</div>
      ) : !error && (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {[
                    { label: 'Member', field: 'full_name' },
                    { label: 'Dojo', field: 'dojo_name' },
                    { label: 'Current Belt', field: 'current_rank_name' },
                    { label: 'Instructor Approval', field: 'instructor_approval' },
                    { label: 'Dues Current', field: 'is_financially_current' },
                    { label: 'Resit Delay Met', field: 'resit_delay_met' },
                    { label: 'Last Grading', field: 'last_grading_date' },
                    { label: 'Last Result', field: 'last_grading_status' },
                    { label: 'Earliest Resit', field: 'earliest_resit_date' },
                    { label: 'Eligible', field: 'is_eligible' },
                  ].map((col) => (
                    <th
                      key={col.field}
                      style={styles.th}
                      onClick={() => toggleSort(col.field as keyof EligibilityRecord)}
                    >
                      {col.label}
                      <span style={styles.sortIcon}>{sortIcon(col.field as keyof EligibilityRecord)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((d, i) => (
                  <tr key={d.profile_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '500', color: '#ffffff' }}>{d.full_name}</div>
                      <div style={{ fontSize: '11px', color: '#666666' }}>{d.member_number || ''}</div>
                    </td>
                    <td style={styles.td}>{d.dojo_name}</td>
                    <td style={styles.td}>
                      <span style={styles.rankBadge}>{d.current_rank_name}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={d.instructor_approval === 'Approved' ? styles.conditionMet : styles.conditionNotMet}>
                        {d.instructor_approval === 'Approved' ? '✅ Approved' : '⏳ Not Approved'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={d.is_financially_current === 1 ? styles.conditionMet : styles.conditionNotMet}>
                        {d.is_financially_current === 1 ? '✅ Yes' : '❌ No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={d.resit_delay_met === 1 ? styles.conditionMet : styles.conditionNotMet}>
                        {d.resit_delay_met === 1 ? '✅ Yes' : '⏳ No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {d.last_grading_date
                        ? new Date(d.last_grading_date).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                    <td style={styles.td}>
                      {d.last_grading_status ? (
                        <span style={
                          d.last_grading_status === 'success' ? styles.statusSuccess :
                          d.last_grading_status === 'probation' ? styles.statusProbation :
                          d.last_grading_status === 'resit' ? styles.statusResit :
                          styles.statusPending
                        }>
                          {d.last_grading_status}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={styles.td}>
                      {d.earliest_resit_date
                        ? new Date(d.earliest_resit_date).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                    <td style={styles.td}>
                      <span style={d.is_eligible === 1 ? styles.eligibleBadge : styles.notEligibleBadge}>
                        {d.is_eligible === 1 ? '✅ Eligible' : '❌ Not Eligible'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginated.length === 0 && (
              <div style={styles.emptyBox}>No members found matching your filters.</div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                {'Showing ' +
                  ((page - 1) * PAGE_SIZE + 1).toLocaleString() +
                  '–' +
                  Math.min(page * PAGE_SIZE, totalCount).toLocaleString() +
                  ' of ' +
                  totalCount.toLocaleString() +
                  ' members'}
              </span>
              <div style={styles.pageButtons}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>First</button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>Prev</button>
                <span style={styles.pageNumber}>{'Page ' + page + ' of ' + totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Next</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Last</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '32px', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' },
  subtitle: { color: '#888888', fontSize: '14px', margin: '0' },
  exportBtn: { backgroundColor: '#1a3a1a', border: '1px solid #22c55e', color: '#22c55e', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  exportBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#666666', padding: '10px 20px', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px' },
  statLabel: { color: '#888888', fontSize: '12px', margin: '0 0 8px 0', fontWeight: '500' },
  statValue: { fontSize: '28px', fontWeight: '700', margin: '0' },
  statSub: { color: '#666666', fontSize: '11px', margin: '4px 0 0 0' },
  retryBtn: { marginLeft: '12px', backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { backgroundColor: '#2d1a1a', border: '1px solid #cc3333', color: '#ff6666', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center' },
  loadingBox: { color: '#888888', textAlign: 'center', padding: '48px' },
  filtersCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' },
  filterLabel: { color: '#888888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', outline: 'none', minWidth: '220px' },
  multiSelectBtn: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  multiSelectDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', minWidth: '180px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', color: '#cccccc', fontSize: '13px', padding: '6px 8px', cursor: 'pointer', borderRadius: '4px' },
  clearBtn: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  tableWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 460px)', borderRadius: '12px', border: '1px solid #2a2a2a', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a1a1a', color: '#888888', padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, cursor: 'pointer', userSelect: 'none' },
  sortIcon: { color: '#cc0000', fontSize: '10px' },
  trEven: { backgroundColor: '#0f0f0f' },
  trOdd: { backgroundColor: '#111111' },
  td: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' },
  rankBadge: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  conditionMet: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  conditionNotMet: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  eligibleBadge: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  notEligibleBadge: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusSuccess: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusProbation: { backgroundColor: '#2d1a0d', color: '#f59e0b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusResit: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusPending: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  emptyBox: { color: '#888888', textAlign: 'center', padding: '48px', backgroundColor: '#0f0f0f' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { color: '#888888', fontSize: '14px' },
  pageButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  pageBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#ffffff', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  pageBtnDisabled: { backgroundColor: '#111111', border: '1px solid #222222', color: '#444444', padding: '8px 14px', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' },
  pageNumber: { color: '#888888', fontSize: '13px', padding: '0 8px' },
};

export default Eligibility;